import { Switch, SwitchProps } from '@main/core-ui';
import React, { useState } from 'react';

import { Popconfirm, PopconfirmProps } from '../Popconfirm';

/**
 * Props for Switch
 */
export interface PopconfirmSwitchProps extends SwitchProps {
  /** The validation status (not passed) */
  validateStatus?: string;
  /** Form id */
  id?: string;
  /** props for the Popconfirm */
  confirmProps?: Omit<PopconfirmProps, 'onConfirm' | 'onCancel'>;
  /** Show confirm when turning on */
  showConfirmWhenTurningOn?: boolean;
  /** Show confirm when turning off */
  showConfirmWhenTurningOff?: boolean;
  /** the html name */
  name?: string;
  /** if the component is controlled by the onChange we don't want to mess with the internal checked state */
  disableCheckOverride?: boolean;
}

/**
 * Switch toggle, will replace the definition above once we remove the rest of
 * the old forms
 */
export const PopconfirmSwitch: React.FC<PopconfirmSwitchProps> = ({
  onChange,
  confirmProps,
  showConfirmWhenTurningOff,
  showConfirmWhenTurningOn,
  disableCheckOverride,
  ...rest
}) => {
  // State used to show/track "future" switch state when confirmation is rendered but has not been accepted
  const [checkOverride, setCheckOverride] = useState<boolean>();
  // State used to save the native event to be passed to onChange when confirmation is accepted.
  const [onChangeEvent, setOnChangeEvent] = useState<MouseEvent>();

  const showConfirm =
    confirmProps &&
    ((rest.checked && showConfirmWhenTurningOff) ||
      (!rest.checked && showConfirmWhenTurningOn));

  const handleSwitchToggle = showConfirm
    ? (checked: boolean, event: MouseEvent): void => {
        // if the component is controlled by the onChange we don't want to mess with the checked state
        if (!disableCheckOverride) setCheckOverride(checked);
        setOnChangeEvent(event);
      }
    : onChange;

  return (
    <Popconfirm
      {...confirmProps}
      onConfirm={() => {
        onChange?.(!!checkOverride, onChangeEvent!);
        setCheckOverride(undefined);
        setOnChangeEvent(undefined);
      }}
      onCancel={() => {
        setCheckOverride(undefined);
        setOnChangeEvent(undefined);
      }}
      disabled={!showConfirm}
    >
      <Switch
        {...rest}
        checked={checkOverride ?? rest.checked}
        onChange={handleSwitchToggle}
        aria-busy={rest.loading ? 'true' : 'false'}
      />
    </Popconfirm>
  );
};
